import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from './config';

// ── User Profile ──────────────────────────────────────────────────────────────
// Trimmed to what AuthContext needs. Reads/writes the SAME `users` collection
// as the main Hustlance app (shared Firebase project) — this is intentionally
// not the full UserProfile shape from the main app's src/firebase/db.ts,
// just the fields the blog actually touches.

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  university?: string;
  profileImage?: string;
  accountType: 'creator' | 'buyer';
  subscriptionPlan?: 'free' | 'creator-pro' | 'elite-creator';
  createdAt: string;
}

export async function createUserProfile(uid: string, data: Omit<UserProfile, 'id'>): Promise<void> {
  await setDoc(doc(db, 'users', uid), { ...data, id: uid });
}

export async function getUserProfile(uid: string): Promise<UserProfile | null> {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? (snap.data() as UserProfile) : null;
}

export async function updateUserProfile(uid: string, updates: Partial<UserProfile>): Promise<void> {
  await updateDoc(doc(db, 'users', uid), updates);
}

// ── Early Access / Newsletter Signups ────────────────────────────────────────
// Reused as-is by NewsletterSignupForm — same collection/rules the main app
// already has, so no new backend is needed for the blog's newsletter block.

export interface EarlyAccessSignup {
  email: string;
  createdAt: string;
  notified: boolean;
}

export async function addEarlyAccessSignup(email: string): Promise<void> {
  await setDoc(
    doc(db, 'earlyAccessSignups', email),
    { email, createdAt: new Date().toISOString(), notified: false },
    { merge: true }
  );
}
